export function newRequestID(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  // getRandomValues remains available on explicitly configured LAN HTTP origins.
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export class APIError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}
type SessionGuard = {
  accountID: string;
  recover: (error: APIError) => Promise<boolean>;
  generation: number;
  pending?: Promise<boolean>;
};
export function createSessionClient() {
  let session: SessionGuard | undefined;

  // Keep recovery separate from routing so an open exercise stays mounted.
  function guardSession(accountID: string, recover: SessionGuard['recover']) {
    const guard: SessionGuard = { accountID, recover, generation: 0 };
    session = guard;
    return () => {
      if (session === guard) session = undefined;
    };
  }
  function confirmSession(accountID: string) {
    if (session?.accountID === accountID) session.generation++;
  }

  async function send<T>(path: string, options: RequestInit, accountID?: string): Promise<T> {
    const controller = new AbortController();
    const abort = () => controller.abort(options.signal?.reason);
    options.signal?.addEventListener('abort', abort, { once: true });
    if (options.signal?.aborted) abort();
    const timeout = setTimeout(
      () => controller.abort(),
      path === '/api/admin/v1/books/import'
        ? 360_000
        : options.body instanceof FormData
          ? 60_000
          : 20_000,
    );
    try {
      const headers = new Headers(options.headers);
      if (!(options.body instanceof FormData)) headers.set('Content-Type', 'application/json');
      if (accountID) headers.set('X-Mastery-Account', accountID);
      const response = await fetch(path, {
        ...options,
        credentials: 'include',
        headers,
        signal: controller.signal,
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new APIError(
          response.status,
          body?.error?.code ?? 'request_failed',
          body?.error?.message ?? '请求失败，请稍后重试',
        );
      }
      return (await response.json()) as T;
    } catch (error) {
      if (options.signal?.aborted) throw options.signal.reason;
      if (controller.signal.aborted)
        throw new APIError(0, 'request_timeout', '连接超时，请稍后重试。');
      if (error instanceof TypeError)
        throw new APIError(0, 'network_error', '网络连接中断，请检查网络后重试。');
      if (error instanceof SyntaxError)
        throw new APIError(0, 'invalid_response', '服务器返回的内容无法读取，请稍后重试。');
      throw error;
    } finally {
      clearTimeout(timeout);
      options.signal?.removeEventListener('abort', abort);
    }
  }

  async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const logout = path === '/api/v1/auth/logout';
    const guard = path.startsWith('/api/v1/auth/') && !logout ? undefined : session;
    const generation = guard?.generation;
    try {
      return await send<T>(path, options, guard?.accountID);
    } catch (error) {
      if (
        !guard ||
        logout ||
        guard !== session ||
        !(error instanceof APIError) ||
        !(error.status === 401 || error.code === 'account_changed') ||
        options.signal?.aborted
      )
        throw error;
      if (generation === guard.generation) {
        guard.pending ??= guard
          .recover(error)
          .then((recovered) => {
            if (recovered) guard.generation++;
            return recovered;
          })
          .finally(() => {
            guard.pending = undefined;
          });
        if (!(await waitForRecovery(guard.pending, options.signal))) throw error;
      }
      if (session !== guard || options.signal?.aborted) throw error;
      // Only an explicit authentication rejection is replayable. Network errors are ambiguous.
      return send<T>(path, options, guard.accountID);
    }
  }
  function waitForRecovery(pending: Promise<boolean>, signal?: AbortSignal | null) {
    if (!signal) return pending;
    return new Promise<boolean>((resolve, reject) => {
      const abort = () => {
        signal.removeEventListener('abort', abort);
        reject(signal.reason);
      };
      if (signal.aborted) {
        reject(signal.reason);
        return;
      }
      signal.addEventListener('abort', abort, { once: true });
      pending.then(
        (value) => {
          signal.removeEventListener('abort', abort);
          resolve(value);
        },
        (error) => {
          signal.removeEventListener('abort', abort);
          reject(error);
        },
      );
    });
  }

  return { request, guardSession, confirmSession };
}
